<template>
    <v-navigation-drawer v-model="isShowBar" fixed clipped app class="base elevation-12" width="300px">
        <v-tabs v-model="tab" background-color="primary" dark class="elevation-1" slider-color="accent" slider-size="3">
            <v-tab v-for="item in tabKeyListFilteredAcl" :key="item.text">
                {{ $t(item.text) }}
            </v-tab>
        </v-tabs>
        <v-tabs-items v-model="tab" class="base">
            <v-tab-item v-for="(lists, i) in menuListFilteredAcl" :key="`tab-${i}`">
                <v-list v-show="!loading" dense>
                    <item-tree v-for="(list, j) in lists" :key="`item-${j}`" :tree="list" />
                </v-list>
            </v-tab-item>
        </v-tabs-items>
        <template v-slot:append>
            <v-list subheader class="pa-0">
                <v-list-item v-for="item in productVersions" :key="item.product">
                    <v-list-item-content>
                        <v-list-item-subtitle class="text-caption">
                            {{ item.product + ' ' + item.version }}
                        </v-list-item-subtitle>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </template>
    </v-navigation-drawer>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { AppContext } from '../../../store/context';
import { ItemTree } from '../../molecules';
const NavigationBar = AppContext.NavigationBar;
type NavigationBarType = AppContext.NavigationBar.Type;
import { State, Action } from 'vuex-class';
import type { UserSettingState } from '../../../store/modules/user_setting/types';
import { treeFilter } from '../../../utils/types/utility';
import type { ApplicationFormTemplateState } from '../../../store/modules/application_form_template/types';
import type { PrivilegeIdLendingRecordState } from '../../../store/modules/privilege_id_lending_record/types';
import type { LicenseState } from '../../../store/modules/license/types';
import type { ProductVersionState, ProductVersion } from '../../../store/modules/product_version/types';

@Component({
    components: {
        ItemTree,
    },
})
export default class TheNavigationSideBar extends Vue {
    @Prop({ default: true }) isShow: boolean;
    @State('userSetting') userSetting: UserSettingState;
    @State('applicationFormTemplate') applicationFormTemplate: ApplicationFormTemplateState;
    @State('privilegeIdLendingRecord') privilegeIdLendingRecord: PrivilegeIdLendingRecordState;
    @State('license') licenseState: LicenseState;
    @State('productVersion') productVersionState: ProductVersionState;
    @Action('show', { namespace: 'userSetting' }) show;
    @Action('count', { namespace: 'applicationFormTemplate' }) count;
    @Action('displayable', { namespace: 'privilegeIdLendingRecord' }) displayable;
    @Action('active', { namespace: 'license' }) active;
    @Action('index', { namespace: 'productVersion' }) index;

    tabKeyNames = [{ text: 'Services' }, { text: 'Admin_Menu', can: 'show AdminMenu' }];
    tab = 0;
    menuLists = [
        NavigationBar.SERVICE_LIST.map(list => JSON.parse(JSON.stringify(list))),
        NavigationBar.ADMIN_MENU_LIST.map(list => JSON.parse(JSON.stringify(list))),
    ];
    isLoad = false;

    /**
     * lifecycle hooks
     */
    async mounted() {
        this.isLoad = true;
        await (this as any).count();
        // ブラウザでリロードした場合に取得した値が消えているので再取得
        if (!this.isFetchedMultiAuthSettingData) {
            await (this as any).show('multifactor');
        }
        if (!this.isFetchedLicenseActiveData) {
            await (this as any).active();
        }
        await (this as any).displayable();
        await (this as any).index();
        this.isLoad = false;
    }

    /**
     * getter, setter
     */
    get isShowBar(): boolean {
        return this.isShow;
    }
    get menuListFilteredAcl(): Array<Array<NavigationBarType>> {
        return this.menuLists
            .filter((lists, i) => {
                const canQuery = this.tabKeyNames[i].can;
                return this.judgeCan(canQuery);
            })
            .map(lists => {
                return treeFilter(lists, v => {
                    if (['Workflow_Management', 'Application', 'Application_Form_List'].includes(v.text)) {
                        return !this.isLiteLicense;
                    }
                    if (['Application', 'Work_Application', 'Draft_and_Waiting_Application'].includes(v.text)) {
                        return this.isApplicationAcl;
                    }
                    if (v.text === 'Privilege_ID_Lending_History') {
                        return this.isPrivilegeIdLendingHistoryDisplayable;
                    }
                    if (v.text === 'Multi-Factor_Authenticate_Device_Management') {
                        return this.isMultifactorAuth;
                    }
                    if (v.text == 'ESS_REC_Linkage_Setting') {
                        return this.isShowRecLickageSetting;
                    }
                    return true;
                });
            });
    }
    get tabKeyListFilteredAcl(): Array<{ text: string; can?: string }> {
        return this.tabKeyNames.filter(lists => {
            const canQuery = lists.can;
            return this.judgeCan(canQuery);
        });
    }
    set isShowBar(val) {
        this.$emit('show-change', val);
    }
    get isFetchedMultiAuthSettingData(): boolean {
        const userSetting = this.userSetting.userSetting;
        if (!userSetting) return false;
        if (!userSetting.multifactor) return false;
        return true;
    }
    get isFetchedLicenseActiveData(): boolean {
        const license = this.licenseState;
        if (!license) return false;
        if (!license.active.activeInfo) return false;
        return true;
    }
    get isMultifactorAuth(): boolean {
        return this.isFetchedMultiAuthSettingData ? this.userSetting.userSetting.multifactor.status !== 'no' : false;
    }
    get isShowRecLickageSetting(): boolean {
        return this.isFetchedLicenseActiveData
            ? this.licenseState.active.activeInfo.pim.edition == 'enterprise'
            : false;
    }
    get isApplicationAcl(): boolean {
        return this.applicationFormTemplate.templateCount > 0;
    }
    get loading(): boolean {
        return this.isLoad;
    }
    get productVersions(): Array<ProductVersion> {
        return this.productVersionState.list;
    }
    get isLiteLicense(): boolean {
        return this.isFetchedLicenseActiveData && this.licenseState.active.activeInfo.pim.edition == 'lite';
    }
    get isPrivilegeIdLendingHistoryDisplayable(): boolean {
        return this.privilegeIdLendingRecord.displayable.isDisplayable;
    }

    /**
     * methods
     */
    judgeCan(canQuery: string): boolean {
        if (!canQuery) return true;
        const canParams = canQuery.split(' ');
        return !!(this as any).$can(canParams[0], canParams[1]);
    }
}
</script>
